import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import { LogLevel, RunLog, RunStatus } from '@rossum/api-client/hooks';
import { Box, Stack, Typography } from '@rossum/ui/material';
import { STATUSES_DATA } from '../columns/StatusColumn';
import { DurationDotIndicator } from '../DurationDotIndicator';
import { Skeletons } from './Skeletons';

const getLogLevelColor = (logLevel: LogLevel) => {
  return logLevel === 'ERROR' || logLevel === 'WARNING'
    ? R.toLowerCase(logLevel)
    : null;
};

type LogsContentProps = {
  logs: RunLog[] | undefined;
  logsAreLoading: boolean;
  runningStatus: Extract<RunStatus, 'running' | 'waiting'> | undefined;
};

export const LogsContent = ({
  logs,
  logsAreLoading,
  runningStatus,
}: LogsContentProps) => {
  const intl = useIntl();

  if (logsAreLoading) {
    return <Skeletons />;
  }

  if (!logs || logs.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {intl.formatMessage({
          id: 'features.activities.rows.logs.noResults',
        })}
      </Typography>
    );
  }

  return (
    <>
      <Stack>
        {logs.map(log => {
          const logId = uuidv4();
          return (
            <Box
              key={logId}
              sx={{
                color: t => {
                  const customColor = getLogLevelColor(log.logLevel);

                  return customColor ? t.palette[customColor].main : 'inherit';
                },
              }}
            >
              {`[${log.timestamp}] ${log.logLevel} ${log.details}`}
            </Box>
          );
        })}
      </Stack>
      {runningStatus ? (
        <Stack direction="row" sx={{ alignItems: 'center' }} spacing={1}>
          <DurationDotIndicator color={STATUSES_DATA[runningStatus].color} />
          <Typography variant="body2" color="text.secondary">
            {intl.formatMessage({
              id: 'features.activities.rows.logs.processing',
            })}
          </Typography>
        </Stack>
      ) : null}
    </>
  );
};
